import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useCallback, useEffect, useRef, useState } from 'react'

import BackButton from '@/components/BackButton'
import Button from '@/components/Button'
import Icon from '@/components/Icon'
import Link from '@/components/Link'

import DefaultComponentProps from '@/types/DefaultComponentProps'

import styles from './Menu.module.scss'
import { useUi } from '@/bus/ui'
import { useUser } from '@/bus/user'
import { routes } from '@/config'
import { useSectionHash } from '@/hooks/sectionHash'

const ITEMS = [
  { id: 'add_networks', icon: 'gramophone/20' },
  { id: 'rent_ads_account', icon: 'time-clock/20' },
  { id: 'deposit_methods', icon: 'card/20' },
  { id: 'agency', icon: 'users/20' },
  { id: 'cpa_network', icon: 'send-message/20' }
] as const

const DESKTOP_MENU_MQ = '(min-width: 1400px)'

const Menu: FC<DefaultComponentProps> = ({ className = '' }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const menuRef = useRef<HTMLElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const { settings } = useUi()
  const { isAuthorized } = useUser()
  const activeSectionId = useSectionHash()

  const isHomePage = router.pathname === routes.HOME

  const closeMenu = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const isDesktopMenu = () => window.matchMedia(DESKTOP_MENU_MQ).matches

    const handlePointerDown = (event: MouseEvent) => {
      if (isDesktopMenu()) {
        return
      }

      const target = event.target as Node

      if (!menuRef.current?.contains(target)) {
        closeMenu()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, closeMenu])

  return (
    <nav ref={menuRef} className={clsx(styles['menu'], className)}>
      {settings.backButton && (
        <BackButton className={styles['menu__back-button']} url={routes.HOME} />
      )}
      <button
        type="button"
        className={clsx(styles['menu__trigger'], {
          [styles['menu__trigger_open']]: isOpen
        })}
        aria-expanded={isOpen}
        aria-controls="main-menu-list"
        aria-label={t('forms:buttons.toggleMenu.label') as string}
        title={t('forms:buttons.toggleMenu.label') as string}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon name={isOpen ? 'cross/24' : 'menu/24'} />
      </button>
      <ul
        id="main-menu-list"
        className={clsx(styles['menu__list'], {
          [styles['menu__list_open']]: isOpen
        })}
      >
        {ITEMS.map(({ id, icon }) => {
          const url = routes[id.toUpperCase()]
          const label = t(`menu:list.${id}.label`)

          return (
            <li key={id} className={styles['menu__item']}>
              <Link
                className={clsx(styles['menu__link'], {
                  [styles['menu__link_active']]: activeSectionId === id
                })}
                href={isHomePage ? `#${id}` : url}
                onClick={closeMenu}
                title={label}
              >
                <div
                  className={clsx(
                    styles['menu__item-icon'],
                    styles[`menu__item-icon_${id}`]
                  )}
                >
                  <Icon
                    name={icon}
                    className={styles['menu__item-icon-img']}
                    width={20}
                    height={20}
                  />
                </div>
                <div className={styles['menu__item-text']}>
                  <span className={styles['menu__item-label']}>{label}</span>
                  <span className={styles['menu__item-summary']}>
                    {t(`menu:list.${id}.summary`)}
                  </span>
                </div>
              </Link>
            </li>
          )
        })}
        {isAuthorized ? (
          <li className={clsx(styles['menu__item'], styles['menu__item_auth'])}>
            <Button
              size={'s'}
              href={{ pathname: routes.SERVICES, spa: false }}
              as={Link}
              className={styles['menu__auth-button']}
              onClick={closeMenu}
            >
              {t('forms:buttons.services.label')}
            </Button>
          </li>
        ) : (
          <>
            <li
              className={clsx(styles['menu__item'], styles['menu__item_auth'])}
            >
              <Button
                variant={'text'}
                as={Link}
                href={routes.LOGIN}
                size={'s'}
                className={styles['menu__auth-button']}
                onClick={closeMenu}
              >
                {t('forms:buttons.signin.label')}
              </Button>
            </li>
            <li
              className={clsx(styles['menu__item'], styles['menu__item_auth'])}
            >
              <Button
                as={Link}
                href={routes.REGISTER}
                size={'s'}
                className={styles['menu__auth-button']}
                onClick={closeMenu}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('forms:buttons.signup.label')
                  }}
                />
              </Button>
            </li>
          </>
        )}
      </ul>
    </nav>
  )
}

export default Menu
